import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  RefreshCw, Send, Search, Sparkles, Loader2, X,
  AlertTriangle, ShieldCheck, CheckCircle, XCircle,
  Play, Pause, Music, ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const QUICK_GREETINGS = [
  { lang: 'ES', label: 'Saludo', text: 'Hola, te habla {name} de OG MUSIC. En que puedo ayudarte hoy?' },
  { lang: 'EN', label: 'Greeting', text: 'Hi, this is {name} from OG MUSIC. How can I help you today?' },
  { lang: 'PT', label: 'Saudacao', text: 'Ola, aqui e {name} do OG MUSIC. Como posso te ajudar hoje?' },
  { lang: 'ES', label: 'Revisando', text: 'Estoy revisando tu caso, dame un momento por favor.' },
  { lang: 'EN', label: 'Checking', text: 'I am looking into your case, please give me a moment.' },
  { lang: 'PT', label: 'Verificando', text: 'Estou verificando seu caso, me de um momento por favor.' },
  { lang: 'ES', label: 'Reemplazo', text: 'OK vamos a verificar el reemplazo, su orden estara bajo validacion por el departamento correspondiente. En la mayor brevedad posible se le estara sustituyendo el tema. Disculpe los inconvenientes.', highlight: true },
  { lang: 'EN', label: 'Replacement', text: 'OK, we will verify the replacement. Your order is under validation by the corresponding department. The track will be replaced as soon as possible. We apologize for any inconvenience.' },
  { lang: 'ES', label: 'Orden enviada', text: 'Tu orden ha sido procesada y los archivos fueron enviados a tu correo. Revisa tu bandeja de entrada y spam.' },
  { lang: 'EN', label: 'Order sent', text: 'Your order has been processed and files were sent to your email. Check your inbox and spam folder.' },
  { lang: 'PT', label: 'Pedido enviado', text: 'Seu pedido foi processado e os arquivos foram enviados para seu email. Verifique sua caixa de entrada e spam.' },
  { lang: 'ES', label: 'Portada OG', text: 'Recuerda que puedes generar tu Portada OG exclusiva desde tu panel. Es unica y solo para ti.' },
  { lang: 'EN', label: 'OG Cover', text: 'Remember you can generate your exclusive OG Cover from your dashboard. It is unique and only for you.' },
  { lang: 'ES', label: 'Despedida', text: 'Fue un placer ayudarte. Si necesitas algo mas, estamos aqui para ti. Gracias por elegir OG MUSIC!' },
  { lang: 'EN', label: 'Goodbye', text: 'It was a pleasure helping you. If you need anything else, we are here for you. Thank you for choosing OG MUSIC!' },
  { lang: 'PT', label: 'Despedida', text: 'Foi um prazer ajudar. Se precisar de mais alguma coisa, estamos aqui. Obrigado por escolher OG MUSIC!' },
];

const SupportPanel = ({ isAdmin = false }) => {
  const [sessions, setSessions] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiHelper, setShowAiHelper] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const chatEndRef = useRef(null);

  // Order & replacement state
  const [chatOrders, setChatOrders] = useState([]);
  const [showOrders, setShowOrders] = useState(false);
  const [verifyReason, setVerifyReason] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(null);

  // Replacement requests for this chat's client
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approveLoading, setApproveLoading] = useState(null);

  // Track picker for replacement
  const [pickerRequestId, setPickerRequestId] = useState(null);
  const [pickerGenre, setPickerGenre] = useState('');
  const [genreTracks, setGenreTracks] = useState([]);
  const [tracksLoading, setTracksLoading] = useState(false);
  const [trackSearch, setTrackSearch] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const [replaceLoading, setReplaceLoading] = useState(null);
  const audioRef = useRef(null);

  // Confirmation modal before sending replacement
  const [confirmModal, setConfirmModal] = useState(null); // { requestId, track, clientName, clientEmail, originalTrack, genre }
  const [replaceDone, setReplaceDone] = useState(false);

  const loadSessions = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/chat-sessions`, { withCredentials: true });
      setSessions(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    loadSessions();
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(r => {
        setAgentName(r.data.name || 'Agente');
        setUserRole(r.data.role || '');
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openChat = async (sessionId) => {
    setActiveChat(sessionId);
    try {
      const r = await axios.get(`${API}/api/admin/chat/${sessionId}`, { withCredentials: true });
      setMessages(r.data);
    } catch (e) { console.error(e); }
  };

  const sendReply = async (text) => {
    const msg = text || reply;
    if (!msg.trim() || !activeChat) return;
    try {
      await axios.post(`${API}/api/admin/chat/${activeChat}/reply`, { message: msg }, { withCredentials: true });
      setReply('');
      openChat(activeChat);
      loadSessions();
    } catch (e) { console.error(e); }
  };

  const sendQuickGreeting = (template) => {
    setReply(template.replace('{name}', agentName));
  };

  // AI Helper
  const askAiHelper = async () => {
    if (!aiQuery.trim()) return;
    setAiLoading(true);
    setAiResponse('');
    try {
      let chatContext = '';
      if (messages.length > 0) {
        chatContext = messages.slice(-5).map(m => `${m.role === 'user' ? 'Cliente' : 'Agente'}: ${m.text}`).join('\n');
      }
      const r = await axios.post(`${API}/api/admin/ai-helper`, { query: aiQuery, chat_context: chatContext }, { withCredentials: true });
      setAiResponse(r.data.response);
    } catch (e) { setAiResponse('Error: ' + (e.response?.data?.detail || e.message)); }
    setAiLoading(false);
  };

  const useAiSuggestion = () => {
    if (aiResponse) setReply(aiResponse);
    setShowAiHelper(false);
    setAiResponse('');
    setAiQuery('');
  };

  const saveAgentName = async () => {
    if (!nameInput.trim()) return;
    try {
      await axios.put(`${API}/api/auth/me`, { name: nameInput.trim() }, { withCredentials: true });
      setAgentName(nameInput.trim());
      setEditingName(false);
    } catch { }
  };

  // ─── Order lookup ───
  const lookupOrders = async (email) => {
    if (!email) return;
    setShowOrders(true);
    try {
      const r = await axios.get(`${API}/api/replacement/chat-orders/${encodeURIComponent(email)}`, { withCredentials: true });
      setChatOrders(r.data.orders || []);
    } catch (e) { setChatOrders([]); }
    // Also load pending replacement requests for this client
    loadClientRequests(email);
  };

  const loadClientRequests = async (email) => {
    try {
      const r = await axios.get(`${API}/api/replacement/requests`, { withCredentials: true });
      const clientReqs = (r.data || []).filter(req =>
        req.user_email?.toLowerCase() === email?.toLowerCase() &&
        ['pending', 'approved'].includes(req.status)
      );
      setPendingRequests(clientReqs);
    } catch { setPendingRequests([]); }
  };

  const markVerification = async (orderId) => {
    setVerifyLoading(orderId);
    try {
      await axios.post(`${API}/api/replacement/mark-verification`, {
        order_id: orderId,
        session_id: activeChat,
        reason: verifyReason || 'Cliente solicita reemplazo via soporte',
      }, { withCredentials: true });
      setVerifyReason('');
      // Auto-send chat message to client
      await axios.post(`${API}/api/admin/chat/${activeChat}/reply`, {
        message: 'Tu orden ha sido marcada en verificacion. Nuestro equipo esta revisando tu caso y te contactaremos en un plazo de 24-48 horas. Disculpa los inconvenientes.',
      }, { withCredentials: true });
      // Refresh
      const sess = sessions.find(s => s.session_id === activeChat);
      if (sess?.email) lookupOrders(sess.email);
      openChat(activeChat);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al marcar verificacion');
    }
    setVerifyLoading(null);
  };

  // ─── Admin: Approve/Reject ───
  const handleApprove = async (requestId, approved) => {
    setApproveLoading(requestId);
    try {
      await axios.post(`${API}/api/replacement/approve/${requestId}`, { approved, admin_note: '' }, { withCredentials: true });
      const sess = sessions.find(s => s.session_id === activeChat);
      if (sess?.email) lookupOrders(sess.email);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setApproveLoading(null);
  };

  // ─── Track picker for replacement (fullscreen, like Pack picker) ───
  const openTrackPicker = async (requestId, genre) => {
    setPickerRequestId(requestId);
    setPickerGenre(genre);
    setTrackSearch('');
    setTracksLoading(true);
    try {
      // Load ALL tracks (we'll group by genre, pre-highlight the matching genre)
      const r = await axios.get(`${API}/api/tracks/?limit=500`, { withCredentials: true });
      const data = r.data;
      setGenreTracks(Array.isArray(data) ? data : (data.tracks || []));
    } catch { setGenreTracks([]); }
    setTracksLoading(false);
  };

  const closeTrackPicker = () => {
    setPickerRequestId(null);
    setGenreTracks([]);
    if (audioRef.current) audioRef.current.pause();
    setPlayingId(null);
  };

  const togglePlay = (trackId, url) => {
    if (playingId === trackId) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(url);
      audio.play();
      audio.onended = () => setPlayingId(null);
      audioRef.current = audio;
      setPlayingId(trackId);
    }
  };

  // Show confirmation modal instead of replacing directly
  const showReplaceConfirm = (requestId, track) => {
    // Find the request to get client info
    const req = pendingRequests.find(r => r.id === requestId);
    setConfirmModal({
      requestId,
      track,
      clientName: req?.user_name || req?.user_email || 'Cliente',
      clientEmail: req?.user_email || '',
      originalTrack: req?.original_track_name || '',
      genre: req?.original_genre || pickerGenre,
    });
  };

  const executeReplace = async () => {
    if (!confirmModal || replaceLoading) return;
    const { requestId, track } = confirmModal;
    setReplaceLoading(track.id);
    try {
      await axios.post(`${API}/api/replacement/replace/${requestId}`, { new_track_id: track.id }, { withCredentials: true });
      // Show success state
      setReplaceDone(true);
      closeTrackPicker();
      if (activeChat) {
        await axios.post(`${API}/api/admin/chat/${activeChat}/reply`, {
          message: 'Tu track ha sido reemplazado exitosamente! Revisa tu correo para los detalles y accede a tu panel para descargar tu nueva musica y re-generar tu Portada OG.',
        }, { withCredentials: true });
        openChat(activeChat);
      }
      const sess = sessions.find(s => s.session_id === activeChat);
      if (sess?.email) lookupOrders(sess.email);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al reemplazar');
      setConfirmModal(null);
    }
    setReplaceLoading(null);
  };

  const closeConfirmModal = () => {
    setConfirmModal(null);
    setReplaceDone(false);
  };

  // Admin approve and immediately open track picker
  const approveAndPick = async (requestId, genre) => {
    setApproveLoading(requestId);
    try {
      await axios.post(`${API}/api/replacement/approve/${requestId}`, { approved: true, admin_note: '' }, { withCredentials: true });
      // Immediately open the track picker for this request
      openTrackPicker(requestId, genre);
      const sess = sessions.find(s => s.session_id === activeChat);
      if (sess?.email) loadClientRequests(sess.email);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setApproveLoading(null);
  };

  // When chat changes
  const onOpenChat = async (sessionId) => {
    setShowOrders(false);
    setChatOrders([]);
    setPendingRequests([]);
    closeTrackPicker();
    await openChat(sessionId);
    const sess = sessions.find(s => s.session_id === sessionId);
    if (sess?.email) lookupOrders(sess.email);
  };

  const filteredTracks = genreTracks.filter(t =>
    !trackSearch ||
    (t.title || '').toLowerCase().includes(trackSearch.toLowerCase()) ||
    (t.artist || '').toLowerCase().includes(trackSearch.toLowerCase())
  );

  // Group tracks by genre for fullscreen picker
  const tracksByGenre = {};
  filteredTracks.forEach(t => {
    const g = t.genre || 'Sin Genero';
    if (!tracksByGenre[g]) tracksByGenre[g] = [];
    tracksByGenre[g].push(t);
  });
  // Sort genres: put the matching genre first
  const sortedGenreKeys = Object.keys(tracksByGenre).sort((a, b) => {
    if (a.toLowerCase() === pickerGenre.toLowerCase()) return -1;
    if (b.toLowerCase() === pickerGenre.toLowerCase()) return 1;
    return a.localeCompare(b);
  });

  const canApprove = isAdmin || userRole === 'admin';

  if (loading) return <p className="text-white/40">Cargando soporte...</p>;

  const needsAttention = sessions.filter(s => s.needs_attention);
  const totalUnread = sessions.reduce((s, c) => s + (c.unread || 0), 0);

  return (
    <div data-testid="support-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold">Chat de Soporte</h2>
          {totalUnread > 0 && (
            <span className="px-2.5 py-1 bg-[#E8720C] rounded-full text-xs font-bold text-white" data-testid="support-unread-badge">
              {totalUnread} sin leer
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Editable agent name */}
          {editingName ? (
            <div className="flex items-center gap-1.5">
              <input
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveAgentName()}
                className="px-2 py-1 bg-white/5 border border-[#E8720C]/30 rounded-lg text-xs text-white w-28 focus:outline-none"
                autoFocus
                data-testid="agent-name-input"
              />
              <button onClick={saveAgentName} className="text-[10px] text-green-400 hover:text-green-300">Guardar</button>
              <button onClick={() => setEditingName(false)} className="text-[10px] text-white/30 hover:text-white/50">Cancelar</button>
            </div>
          ) : (
            <button
              onClick={() => { setNameInput(agentName); setEditingName(true); }}
              className="px-2.5 py-1 bg-white/5 hover:bg-white/10 rounded-lg text-xs text-white/60 hover:text-white/80 transition-colors"
              title="Haz clic para cambiar tu nombre de agente"
              data-testid="agent-name-display"
            >
              {agentName}
            </button>
          )}
          <button onClick={loadSessions} className="p-2 hover:bg-white/10 rounded-lg text-white/40"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {needsAttention.length > 0 && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3" data-testid="support-alert">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-400">{needsAttention.length} chat(s) esperando atencion humana</p>
            <p className="text-xs text-white/40">Clientes que necesitan ayuda de un agente real</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sessions List */}
        <div className="glass-card p-4 lg:col-span-1 max-h-[700px] overflow-y-auto">
          <h3 className="font-semibold text-sm mb-4">Conversaciones ({sessions.length})</h3>
          {sessions.length === 0 ? (
            <p className="text-sm text-white/40 text-center py-8">Sin conversaciones</p>
          ) : sessions.map(s => (
            <button key={s.session_id} onClick={() => onOpenChat(s.session_id)}
              className={`w-full text-left p-3 rounded-lg mb-2 transition-colors ${activeChat === s.session_id ? 'bg-[#E8720C]/20 border border-[#E8720C]/30' : s.needs_attention ? 'bg-red-500/10 border border-red-500/20' : 'bg-white/5 hover:bg-white/10'}`}
              data-testid={`chat-session-${s.session_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-sm truncate">{s.name}</span>
                  {s.escalated && !s.has_human_reply && (
                    <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded text-[9px] font-bold uppercase animate-pulse shrink-0">Espera Humano</span>
                  )}
                </div>
                {s.unread > 0 && <span className="w-5 h-5 bg-[#E8720C] rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">{s.unread}</span>}
              </div>
              <p className="text-xs text-white/40 truncate mt-1">{s.last_message}</p>
              {s.email && <p className="text-[10px] text-white/30 mt-1">{s.email}</p>}
            </button>
          ))}
        </div>

        {/* Chat Area + Replacement Flow */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          {/* Chat Messages */}
          <div className="glass-card p-4 flex flex-col" style={{ height: showAiHelper ? '400px' : '550px' }}>
            {!activeChat ? (
              <div className="flex-1 flex items-center justify-center text-white/30 text-sm">Selecciona una conversacion</div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto space-y-3 mb-3 pr-2">
                  {messages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                        msg.role === 'user' ? 'bg-white/5 text-white/80 rounded-bl-md' : 'bg-[#E8720C] text-white rounded-br-md'
                      }`}>
                        {msg.role === 'user' && <p className="text-[10px] text-white/40 mb-1">{msg.name}</p>}
                        {msg.text}
                        <p className="text-[9px] opacity-50 mt-1">{new Date(msg.timestamp).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>

                {/* Quick Replies */}
                <div className="mb-2 flex flex-wrap gap-1.5" data-testid="quick-replies-bar">
                  {QUICK_GREETINGS.map((g, i) => (
                    <button key={i} onClick={() => sendQuickGreeting(g.text)}
                      className={`px-2.5 py-1.5 border rounded-lg text-[10px] transition-all ${
                        g.highlight
                          ? 'bg-yellow-500/10 hover:bg-yellow-500/20 border-yellow-500/20 hover:border-yellow-500/30 text-yellow-400 hover:text-yellow-300'
                          : 'bg-white/[0.04] hover:bg-[#E8720C]/15 border-white/5 hover:border-[#E8720C]/20 text-white/50 hover:text-white/80'
                      }`} data-testid={`quick-reply-${i}`}>
                      <span className={`text-[8px] font-bold mr-1 ${g.highlight ? 'text-yellow-500' : 'text-[#E8720C]'}`}>{g.lang}</span>{g.label}
                    </button>
                  ))}
                </div>

                {/* ═══ ORDERS + REPLACEMENT FLOW ═══ */}
                {showOrders && chatOrders.length > 0 && (
                  <div className="mb-2 p-3 bg-white/[0.02] border border-white/10 rounded-xl" data-testid="order-verification-section">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5 text-yellow-400" />
                        <span className="text-[10px] font-bold text-yellow-400 uppercase">Ordenes del cliente</span>
                      </div>
                      <button onClick={() => setShowOrders(false)} className="text-white/30 hover:text-white/50"><X className="w-3 h-3" /></button>
                    </div>

                    <div className="space-y-1.5 max-h-[100px] overflow-y-auto">
                      {chatOrders.map(order => (
                        <div key={order.id} className="flex items-center gap-2 p-2 bg-black/20 rounded-lg text-[10px]" data-testid={`order-item-${order.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-white/80 font-medium truncate">{order.item_name}</p>
                            <p className="text-white/30">${order.amount?.toFixed(2)} | Genero: <span className="text-[#E8720C]">{order.genre || 'N/A'}</span></p>
                          </div>
                          {order.replacement_status === 'under_verification' ? (
                            <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-[9px] font-bold shrink-0">En verificacion</span>
                          ) : order.replacement_status === 'completed' ? (
                            <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-[9px] font-bold shrink-0">Reemplazado</span>
                          ) : (
                            <button
                              onClick={() => markVerification(order.order_id)}
                              disabled={verifyLoading === order.order_id}
                              className="px-2.5 py-1 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded text-[9px] font-bold shrink-0 transition-colors flex items-center gap-1 disabled:opacity-50"
                              data-testid={`mark-verification-${order.id}`}
                            >
                              {verifyLoading === order.order_id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <AlertTriangle className="w-2.5 h-2.5" />}
                              Enviar a Verificacion
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    <input
                      value={verifyReason}
                      onChange={e => setVerifyReason(e.target.value)}
                      placeholder="Razon del reclamo (opcional)..."
                      className="w-full mt-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-[10px] text-white placeholder-white/20 focus:outline-none focus:border-yellow-500/30"
                      data-testid="verify-reason-input"
                    />

                    {/* ─── Pending replacement requests for this client ─── */}
                    {pendingRequests.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/5 space-y-2" data-testid="replacement-requests-inline">
                        <p className="text-[10px] font-bold text-[#E8720C] uppercase">Solicitudes de reemplazo activas</p>
                        {pendingRequests.map(req => (
                          <div key={req.id} className={`p-2.5 rounded-lg border ${
                            req.status === 'approved' ? 'bg-blue-500/5 border-blue-500/20' : 'bg-yellow-500/5 border-yellow-500/20'
                          }`} data-testid={`inline-request-${req.id}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${
                                    req.status === 'approved' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'
                                  }`}>{req.status === 'approved' ? 'Aprobado' : 'Pendiente'}</span>
                                  <span className="text-[10px] text-white/70 font-medium truncate">{req.original_track_name}</span>
                                  <span className="text-[9px] text-[#E8720C]">{req.original_genre}</span>
                                  {req.client_total_replacements > 1 && (
                                    <span className={`px-1 py-0.5 rounded text-[8px] font-bold ${
                                      req.client_total_replacements >= 3 ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                                    }`}>{req.client_total_replacements}x</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                {req.status === 'pending' && canApprove && (
                                  <>
                                    <button
                                      onClick={() => approveAndPick(req.id, req.original_genre || '')}
                                      disabled={approveLoading === req.id}
                                      className="px-2 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded text-[9px] font-bold flex items-center gap-1 disabled:opacity-50"
                                      data-testid={`approve-inline-${req.id}`}
                                    >
                                      {approveLoading === req.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <CheckCircle className="w-2.5 h-2.5" />}
                                      Aprobar y Reemplazar
                                    </button>
                                    <button
                                      onClick={() => handleApprove(req.id, false)}
                                      disabled={approveLoading === req.id}
                                      className="px-2 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-[9px] font-bold flex items-center gap-1 disabled:opacity-50"
                                      data-testid={`reject-inline-${req.id}`}
                                    >
                                      <XCircle className="w-2.5 h-2.5" />
                                      Rechazar
                                    </button>
                                  </>
                                )}
                                {req.status === 'approved' && (
                                  <button
                                    onClick={() => openTrackPicker(req.id, req.original_genre || '')}
                                    className="px-2.5 py-1 bg-[#E8720C]/20 hover:bg-[#E8720C]/30 text-[#E8720C] rounded text-[9px] font-bold flex items-center gap-1"
                                    data-testid={`pick-track-inline-${req.id}`}
                                  >
                                    <Music className="w-2.5 h-2.5" />
                                    Seleccionar Reemplazo
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Reply Bar */}
                <div className="flex gap-2 border-t border-white/5 pt-3">
                  <input value={reply} onChange={e => setReply(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && sendReply()}
                    placeholder="Escribe una respuesta..."
                    className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                    data-testid="support-chat-reply-input" />
                  <button onClick={() => sendReply()} className="w-10 h-10 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl flex items-center justify-center text-white shrink-0" data-testid="support-chat-send-btn">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* AI Internal Helper */}
          <div className="glass-card p-4" data-testid="ai-helper-panel">
            <button onClick={() => setShowAiHelper(!showAiHelper)} className="w-full flex items-center justify-between" data-testid="ai-helper-toggle">
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-[#E8720C]" />
                  <span className="text-sm font-medium text-white/60">Asistente AI interno</span>
                </div>
                <span className="text-[10px] text-white/30">{showAiHelper ? 'Ocultar' : 'Abrir'}</span>
              </button>
              {showAiHelper && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <p className="text-[10px] text-white/30 mb-2">Preguntame lo que necesites: traducir mensajes, sugerir respuestas, buscar info del ecosistema...</p>
                  {aiResponse && (
                    <div className="mb-3 p-3 bg-[#E8720C]/5 border border-[#E8720C]/10 rounded-xl" data-testid="ai-helper-response">
                      <p className="text-sm text-white/80 whitespace-pre-wrap">{aiResponse}</p>
                      <button onClick={useAiSuggestion} className="mt-2 px-3 py-1.5 bg-[#E8720C]/20 hover:bg-[#E8720C]/30 text-[#E8720C] rounded-lg text-xs font-medium" data-testid="ai-use-suggestion">
                        Usar como respuesta
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input value={aiQuery} onChange={e => setAiQuery(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && askAiHelper()}
                      placeholder="Ej: Traduce al ingles 'Tu orden esta en camino'"
                      className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                      data-testid="ai-helper-input" />
                    <button onClick={askAiHelper} disabled={aiLoading}
                      className="px-4 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-medium text-white flex items-center gap-1.5 disabled:opacity-50 shrink-0" data-testid="ai-helper-send">
                      {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      Preguntar
                    </button>
                  </div>
                </div>
              )}
            </div>
        </div>
      </div>

      {/* ═══ FULLSCREEN TRACK PICKER MODAL (like Pack picker) ═══ */}
      {pickerRequestId && (
        <div className="fixed inset-0 bg-black/95 z-[60] flex flex-col" data-testid="track-picker-modal"
          onKeyDown={e => { if (e.key === 'Escape') closeTrackPicker(); }}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#0a0a0e] shrink-0">
            <div className="flex items-center gap-4">
              <button onClick={closeTrackPicker} className="p-2 rounded-lg hover:bg-white/10 text-white/50" data-testid="picker-back">
                <X className="w-5 h-5" />
              </button>
              <div>
                <h3 className="text-lg font-bold">Seleccionar Track de Reemplazo</h3>
                <p className="text-xs text-[#E8720C]">Genero original: {pickerGenre || 'Todos'} — Selecciona un track para reemplazar</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 w-72 focus:outline-none focus:border-[#E8720C]/50"
                placeholder="Buscar por nombre o artista..."
                value={trackSearch} onChange={e => setTrackSearch(e.target.value)} data-testid="picker-search" autoFocus />
              <button onClick={closeTrackPicker}
                className="px-6 py-2.5 bg-white/10 hover:bg-white/15 rounded-xl text-sm font-bold text-white/70">
                Cancelar
              </button>
            </div>
          </div>

          {/* Track List grouped by genre */}
          <div className="flex-1 overflow-y-auto">
            {tracksLoading ? (
              <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-[#E8720C]" /></div>
            ) : sortedGenreKeys.length === 0 ? (
              <div className="flex items-center justify-center h-full text-white/30">No hay tracks disponibles</div>
            ) : (
              sortedGenreKeys.map(genre => {
                const isMatchingGenre = genre.toLowerCase() === pickerGenre.toLowerCase();
                return (
                  <div key={genre}>
                    <div className={`sticky top-0 z-10 px-6 py-2.5 border-b border-white/[0.06] flex items-center justify-between ${
                      isMatchingGenre ? 'bg-[#E8720C]/10' : 'bg-[#111114]'
                    }`}>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-bold ${isMatchingGenre ? 'text-[#E8720C]' : 'text-white/60'}`}>{genre}</span>
                        <span className="text-xs text-white/30">{tracksByGenre[genre].length} tracks</span>
                        {isMatchingGenre && <span className="px-2 py-0.5 bg-[#E8720C]/20 text-[#E8720C] rounded text-[9px] font-bold">GENERO ORIGINAL</span>}
                      </div>
                    </div>
                    {tracksByGenre[genre].map((t, i) => (
                      <div key={t.id}
                        className={`flex items-center gap-4 px-6 py-3.5 cursor-pointer transition-colors border-b border-white/[0.04] group ${
                          i % 2 === 0 ? 'bg-transparent hover:bg-white/[0.03]' : 'bg-white/[0.015] hover:bg-white/[0.04]'
                        }`}
                        data-testid={`picker-track-${t.id}`}>
                        {/* Play button */}
                        {t.audio_preview_url ? (
                          <button onClick={(e) => { e.stopPropagation(); togglePlay(t.id, t.audio_preview_url); }}
                            className="w-8 h-8 rounded-full bg-white/5 hover:bg-[#E8720C]/20 flex items-center justify-center shrink-0">
                            {playingId === t.id ? <Pause className="w-3.5 h-3.5 text-[#E8720C]" /> : <Play className="w-3.5 h-3.5 text-white/50 ml-0.5" />}
                          </button>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0"><Music className="w-3.5 h-3.5 text-white/20" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-white/80">{t.title}</span>
                          <span className="text-sm text-white/30 ml-3">{t.artist}</span>
                        </div>
                        <span className="text-xs text-white/30 w-16 text-right shrink-0">{t.bpm > 0 ? `${t.bpm} BPM` : ''}</span>
                        <button
                          onClick={() => showReplaceConfirm(pickerRequestId, t)}
                          disabled={replaceLoading === t.id}
                          className="px-4 py-2 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-lg text-xs font-bold opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50 shrink-0 flex items-center gap-1.5"
                          data-testid={`replace-with-${t.id}`}
                        >
                          {replaceLoading === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                          Reemplazar
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ═══ CONFIRMATION MODAL — Preview before sending replacement ═══ */}
      {confirmModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => !replaceLoading && !replaceDone && closeConfirmModal()}>
          <div className="w-full max-w-lg mx-4 bg-[#0c0c10] border border-white/10 rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-testid="replace-confirm-modal">

            {replaceDone ? (
              /* ── SUCCESS STATE ── */
              <div className="p-8 text-center" data-testid="replace-success">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Reemplazo Enviado!</h3>
                <p className="text-sm text-white/60 mb-1">El track ha sido reemplazado exitosamente.</p>
                <p className="text-sm text-white/60 mb-6">Se envio el correo de confirmacion a <strong className="text-white">{confirmModal.clientEmail}</strong></p>
                <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-xl mb-6">
                  <p className="text-xs text-green-400/80">
                    <strong>{confirmModal.originalTrack}</strong> → <strong>{confirmModal.track.title}</strong>
                  </p>
                </div>
                <button onClick={closeConfirmModal}
                  className="px-8 py-3 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-xl font-bold text-sm transition-colors"
                  data-testid="replace-success-close">
                  Cerrar
                </button>
              </div>
            ) : (
              /* ── PREVIEW STATE ── */
              <>
                {/* Header */}
                <div className="px-6 py-4 border-b border-white/10 bg-[#0a0a0e]">
                  <h3 className="text-lg font-bold text-white">Confirmar Reemplazo</h3>
                  <p className="text-xs text-white/40 mt-0.5">Revisa el correo que se le enviara al cliente</p>
                </div>

                <div className="p-6 space-y-4 max-h-[65vh] overflow-y-auto">
                  {/* Client Profile + Summary */}
                  <div className="flex items-center gap-4 p-4 bg-white/[0.03] border border-white/10 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-[#E8720C]/20 flex items-center justify-center text-[#E8720C] font-bold text-sm shrink-0">
                      {(confirmModal.clientName || 'C')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white">{confirmModal.clientName}</p>
                      <p className="text-xs text-white/40">{confirmModal.clientEmail}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[9px] text-white/30 uppercase">Reemplazo</p>
                      <p className="text-xs text-white/50 line-through">{confirmModal.originalTrack}</p>
                      <p className="text-xs text-[#E8720C] font-semibold">{confirmModal.track.title}</p>
                    </div>
                  </div>

                  {/* Real Email Preview in iframe */}
                  <div className="rounded-xl overflow-hidden border border-[#E8720C]/20" data-testid="email-preview-container">
                    <div className="px-4 py-2 bg-[#E8720C]/10 flex items-center gap-2">
                      <Send className="w-3 h-3 text-[#E8720C]" />
                      <span className="text-[10px] text-[#E8720C] font-bold uppercase tracking-wider">Correo que recibira el cliente</span>
                    </div>
                    <iframe
                      src={`${API}/api/replacement/preview/success-email-dynamic?buyer_name=${encodeURIComponent(confirmModal.clientName)}&old_track=${encodeURIComponent(confirmModal.originalTrack)}&new_track=${encodeURIComponent(confirmModal.track.title)}&genre=${encodeURIComponent(confirmModal.genre)}`}
                      className="w-full bg-[#050507]"
                      style={{ height: '480px', border: 'none' }}
                      title="Preview del correo"
                      data-testid="email-preview-iframe"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="px-6 py-4 border-t border-white/10 bg-[#0a0a0e] flex items-center justify-between">
                  <button onClick={closeConfirmModal} disabled={replaceLoading}
                    className="px-5 py-2.5 text-sm text-white/50 hover:text-white/80 transition-colors disabled:opacity-30">
                    Cancelar
                  </button>
                  <button onClick={executeReplace} disabled={replaceLoading}
                    className="px-8 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-xl font-bold text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
                    data-testid="confirm-replace-send">
                    {replaceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {replaceLoading ? 'Enviando...' : 'Enviar Reemplazo'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportPanel;
